import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getMessages, sendWebMessage } from '../../services/api';
import storage from '../../utils/storage';

// Async thunk for fetching messages
export const fetchMessages = createAsyncThunk(
  'messages/fetchAll',
  async (conversationId, { rejectWithValue, getState }) => {
    try {
      const response = await getMessages(conversationId);
      const { userMapping } = getState().conversations;
      const { user } = getState().auth;
      
      if (response.data) {
        return response.data.map(msg => ({
          id: msg._id,
          sender: msg.sender === user?._id ? 'Me' : userMapping[msg.sender] || 'Unknown',
          text: msg.text,
          time: new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isMe: msg.sender === user?._id,
          conversationId
        }));
      }
      throw new Error('Failed to fetch messages');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for sending messages
export const sendMessage = createAsyncThunk(
  'messages/send',
  async ({ conversationId, text, receiver }, { rejectWithValue, getState }) => {
    try {
      const userId = await storage.getItem('userId');
      const response = await sendWebMessage({
        conversationId: conversationId,
        text,
        reciever: receiver,
        sender: userId
      });
      
      return {
        id: Date.now(), // Temporary ID until socket updates
        sender: 'Me',
        text,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isMe: true,
        conversationId
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  messages: [],
  loading: false,
  error: null,
  sendingMessage: false
};

const messageSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    addMessage: (state, action) => {
      const newMessage = action.payload;
      // Check if we should group with last message
      const lastGroup = state.messages[state.messages.length - 1];
      const canGroupWithLast = lastGroup && 
                              lastGroup.isMe === newMessage.isMe && 
                              lastGroup.conversationId === newMessage.conversationId &&
                              (Date.now() - new Date(lastGroup.messages[lastGroup.messages.length - 1].time).getTime()) < 300000;

      if (canGroupWithLast) {
        lastGroup.messages.push(newMessage);
      } else {
        state.messages.push({
          sender: newMessage.sender,
          isMe: newMessage.isMe,
          conversationId: newMessage.conversationId,
          messages: [newMessage]
        });
      }
    },
    clearMessages: (state) => {
      state.messages = [];
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch messages cases
      .addCase(fetchMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading = false;
        // Group messages
        state.messages = action.payload.reduce((acc, msg) => {
          const lastGroup = acc[acc.length - 1];
          const canGroupWithLast = lastGroup && 
                                 lastGroup.isMe === msg.isMe && 
                                 lastGroup.conversationId === msg.conversationId &&
                                 (new Date(msg.time) - new Date(lastGroup.messages[lastGroup.messages.length - 1].time)) < 300000;

          if (canGroupWithLast) {
            lastGroup.messages.push(msg);
          } else {
            acc.push({
              sender: msg.sender,
              isMe: msg.isMe,
              conversationId: msg.conversationId,
              messages: [msg]
            });
          }
          return acc;
        }, []);
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Send message cases
      .addCase(sendMessage.pending, (state) => {
        state.sendingMessage = true;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sendingMessage = false;
        // Message will be added through socket to ensure consistency
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.sendingMessage = false;
        state.error = action.payload;
      });
  }
});

export const { addMessage, clearMessages } = messageSlice.actions;

export default messageSlice.reducer; 